import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  Dialog,
  MenuItem,
  Select,
  Theme,
} from '@material-ui/core';
import Button from '../atoms/Button';
import { makeStyles } from '@material-ui/core/styles';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import NamedBox from '../atoms/NamedBox';
import CloseButton from '../atoms/CloseButton';
import { COLOR_BACKGROUND_LIGHT, COLOR_WHITE } from '../styles/colors';
import {
  ProfileStatsCategory,
  PROFILE_STATS_CATEGORIES_NON_NFL_LOCKED,
  PROFILE_STATS_CATEGORIES_NFL_LOCKED,
  PROFILE_STATS_CATEGORIES_ROWS_COUNT,
} from '../services/profile-stats-categories';

interface ProfileStatsCategoriesModalProps {
  open: boolean;
  onClose: () => void;
  options: ProfileStatsCategory[];
  values: ProfileStatsCategory[];
  isNFL?: boolean;
  onChange: (values:ProfileStatsCategory[]) => void;
  getOptionLabel?: (value:ProfileStatsCategory) => string;
}

type DraftProfileStatsCategory = ProfileStatsCategory | '';

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    minWidth: 420,
    padding: theme.spacing(1, 2, 2),
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.25),
    // border: `1px solid ${theme.palette.action.selected}`,
    // borderRadius: 8,
    // padding: theme.spacing(0.5, 0.75),
    background: COLOR_WHITE,
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',

    '&:hover': {
      '& $dragHandle': {
        opacity: 1,
      },
    },
  },
  rowEven: {
    background: COLOR_BACKGROUND_LIGHT,
  },
  rowOdd: {
    background: COLOR_WHITE,
  },
  dragHandle: {
    cursor: 'grab',
    color: theme.palette.text.secondary,
    display: 'flex',
    // marginRight: theme.spacing(1),
    // padding: theme.spacing(0.4, 0.5),
    opacity: 0,
    transition: 'opacity 0.15s ease',
  },
  select: {
    flex: 1,
    '& .MuiSelect-select': {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  dragging: {
    boxShadow: theme.shadows[4],
    borderColor: theme.palette.text.secondary,
    transform: 'scale(1.01)',
  },
  closeButton: {
    marginLeft: 'auto',
  },
  actions: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
}), { name: ProfileStatsCategoriesModal.name });

export default function ProfileStatsCategoriesModal (props: ProfileStatsCategoriesModalProps) {
  const {
    open,
    onClose,
    options,
    values,
    isNFL = false,
    onChange,
    getOptionLabel,
  } = props;
  const classes = useStyles();

  const ROWS_COUNT = PROFILE_STATS_CATEGORIES_ROWS_COUNT;
  const lockedCategories = isNFL ? PROFILE_STATS_CATEGORIES_NFL_LOCKED : PROFILE_STATS_CATEGORIES_NON_NFL_LOCKED;

  const normalizedValues = useMemo<DraftProfileStatsCategory[]>(() => {
    const nextValues: DraftProfileStatsCategory[] = values.slice(0, ROWS_COUNT);
    while (nextValues.length < ROWS_COUNT) {
      nextValues.push('');
    }

    return nextValues;
  }, [values, ROWS_COUNT]);

  const [draftValues, setDraftValues] = useState<DraftProfileStatsCategory[]>(normalizedValues);

  useEffect(() => {
    if (open) {
      setDraftValues(normalizedValues);
    }
  }, [open, normalizedValues]);

  function isLockedRow (selectedValue:DraftProfileStatsCategory) {
    return !!selectedValue && lockedCategories.includes(selectedValue);
  }

  function getRowOptions (selectedValue:DraftProfileStatsCategory) {
    if (isLockedRow(selectedValue)) {
      return options;
    }

    return options.filter((option:ProfileStatsCategory) => !lockedCategories.includes(option));
  }

  function isOptionDisabled (option:ProfileStatsCategory, rowIndex:number) {
    return draftValues.some((selectedValue:DraftProfileStatsCategory, index:number) => index !== rowIndex && selectedValue === option);
  }

  function onChangeRow (rowIndex:number, selectedValue:DraftProfileStatsCategory) {
    const nextValues = [...draftValues];

    nextValues[rowIndex] = selectedValue;
    setDraftValues(nextValues);
  }

  function onDragEnd (result:DropResult) {
    if (!result.destination) return;

    const nextValues = [...draftValues];

    const [moved] = nextValues.splice(result.source.index, 1);
    nextValues.splice(result.destination.index, 0, moved);
    setDraftValues(nextValues);
  }

  function onCancel () {
    setDraftValues(normalizedValues);
    onClose();
  }

  function onApply () {
    onChange(draftValues.filter((value): value is ProfileStatsCategory => Boolean(value)));
    onClose();
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <NamedBox
        name='Add / Remove Columns'
        headerContent={<CloseButton className={classes.closeButton} onClick={onCancel} />}
      >
        <div className={classes.content}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="profile-stats-categories-modal">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {draftValues.map((selectedValue:DraftProfileStatsCategory, index:number) => (
                    <Draggable
                      key={`profile-stats-category-${index}`}
                      draggableId={`profile-stats-category-${index}`}
                      index={index}
                    >
                      {({ draggableProps, dragHandleProps, innerRef }, snapshot) => {
                        const rowOptions = getRowOptions(selectedValue);
                        const selectDisabled = isLockedRow(selectedValue);

                        return (
                        <div
                          ref={innerRef}
                          {...draggableProps}
                          className={clsx(
                            classes.row,
                            index % 2 === 0 ? classes.rowOdd : classes.rowEven,
                            snapshot.isDragging && classes.dragging,
                          )}
                        >
                          <div className={classes.dragHandle} {...dragHandleProps}>
                            <DragIndicatorIcon fontSize="small" />
                          </div>

                          <Select
                            className={classes.select}
                            variant="outlined"
                            value={selectedValue}
                            disabled={selectDisabled}
                              onChange={(event:any) => onChangeRow(index, event.target.value as DraftProfileStatsCategory)}
                            displayEmpty
                          >
                            {rowOptions.map((option:ProfileStatsCategory) => (
                              <MenuItem
                                key={option}
                                value={option}
                                disabled={isOptionDisabled(option, index)}
                              >
                                {getOptionLabel ? getOptionLabel(option) : option}
                              </MenuItem>
                            ))}
                          </Select>
                        </div>
                        );
                      }}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div className={classes.actions}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button primary onClick={onApply}>Apply</Button>
          </div>
        </div>
      </NamedBox>
    </Dialog>
  );
}
